import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";

import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from "./tracking";

import Hero from "views/Common/Hero";
import Top5 from "views/Common/Top5";
import ImageSlider from "views/ImageSlider";
import VIP from "./VIP";

import imgArrowRight from "assets/images/_sff/icon-arrow-right.svg";
import imgTapeDummy from "assets/images/_sff/dummy-movie-tape.png";

const images = [
  "/images/slider/1.png",
  "/images/slider/2.png",
  "/images/slider/3.png",
  "/images/slider/4.png",
  "/images/slider/5.png",
  "/images/slider/6.png",
  "/images/slider/7.png",
  "/images/slider/8.png",
  "/images/slider/9.png",
  "/images/slider/10.png",
  "/images/slider/11.png",
  "/images/slider/12.png",
  "/images/slider/13.png",
  "/images/slider/14.png",
  "/images/slider/15.png",
  "/images/slider/16.png",
  "/images/slider/17.png",
  "/images/slider/18.png",
  "/images/slider/19.png",
  "/images/slider/20.png",
  "/images/slider/21.png",
  "/images/slider/22.png",
  "/images/slider/23.png",
  "/images/slider/24.png",
  "/images/slider/25.png",
  "/images/slider/26.png",
  "/images/slider/27.png",
  "/images/slider/28.png",
  "/images/slider/29.png",
  "/images/slider/30.png",
  "/images/slider/1.png",
  "/images/slider/2.png",
  "/images/slider/3.png",
  "/images/slider/4.png",
  "/images/slider/5.png",
  "/images/slider/6.png",
  "/images/slider/7.png",
  "/images/slider/8.png",
  "/images/slider/9.png",
  "/images/slider/10.png",

  // Add more image URLs here
];

function Home({ baseDomain, isVip }) {
  const [isBadgeVisible, setIsBadgeVisible] = useState(false);

  const { t, i18n } = useTranslation();

  const listenToScroll = () => {
    let heightToHideFrom = 100;
    let heightToHideTo = 900;
    const winScroll =
      document.body.scrollTop || document.documentElement.scrollTop;

    if (winScroll > heightToHideFrom && winScroll < heightToHideTo) {
      setIsBadgeVisible(true);
    } else {
      setIsBadgeVisible(false);
    }
  };

  const ExpandableText = ({ text }) => {
    // Use state to track whether the text should be expanded or not
    const [expanded, setExpanded] = useState(false);

    // Function to handle "read more" link click
    const handleReadMoreClick = () => {
      setExpanded(!expanded);
    };

    // Split the text by </p> tags
    const paragraphs = text.split("</p>");

    // Filter out any empty strings resulting from the split
    const filteredParagraphs = paragraphs.filter(
      (paragraph) => paragraph.trim() !== ""
    );

    // Render the first paragraph and "read more" link if there are more paragraphs
    return (
      <div className="readmore-section">
        <div
          dangerouslySetInnerHTML={{ __html: filteredParagraphs[0] + "</p>" }}
        />
        {filteredParagraphs.length > 1 && (
          <div>
            {!expanded ? (
              <a className="read-more" onClick={handleReadMoreClick}>
                {t("Readmore")}
              </a>
            ) : (
              filteredParagraphs
                .slice(1)
                .map((paragraph, index) => (
                  <div
                    key={index}
                    dangerouslySetInnerHTML={{ __html: paragraph + "</p>" }}
                  />
                ))
            )}
          </div>
        )}
      </div>
    );
  };

  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-width: 800px)",
  });

  useEffect(() => {
    window.addEventListener("scroll", listenToScroll);

    return () => window.removeEventListener("scroll", listenToScroll);
  }, []);

  return (
    <>
      <Hero t={t} layout="home">
        <div className="description narrower mx-auto text-center mb-5 mb-md-6">
          <h1
            dangerouslySetInnerHTML={{
              __html: t("HomeTitle"),
            }}
          />
          <p className="px-3 px-md-5">
            {isVip ? t("VIPHomeParagraph") : t("HomeParagraph")}
          </p>
          <Link to={isVip ? "/vip/quiz" : "/quiz"}>
            <button
              onClick={() =>
                trackEvent("user_interaction", "click", "take_quiz", "main")
              }
              className={`btn btn-orange text-white text-uppercase d-block fw-bold mt-2 gradient-orange mx-auto`}>
              <div className="d-flex align-items-center">
                {t("HomeCTA")} <img src={imgArrowRight} />
              </div>
            </button>
          </Link>

          <button className="btn text-white text-uppercase d-block small fw-bold mt-2 mx-auto d-none">
            {t("FindMore")}
          </button>
        </div>

        <ImageSlider images={images} />
      </Hero>
      <div className="section-usps my-5 my-md-6">
        <div className="container text-center">
          <div className="row g-3 g-md-3">
            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="24"
                  viewBox="0 0 22 24"
                  fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.5138 1.43499C18.4036 1.3538 18.2702 1.31007 18.1333 1.31023L18.133 1.3109C18.0677 1.311 18.0029 1.32106 17.9406 1.34072L14.812 2.31953L16.1235 3.59968L19.0356 2.68869L18.7457 1.76135C18.7053 1.63056 18.624 1.51618 18.5138 1.43499ZM10.0164 3.81984L13.3327 2.78233L14.6442 4.06246L11.3279 5.09992L10.0164 3.81984ZM8.53714 4.28265L9.84858 5.56271L6.53215 6.6002L5.22083 5.32017L8.53714 4.28265ZM3.74156 5.78297L5.05286 7.06298L2.01873 8.01216L1.72865 7.08566C1.67805 6.92311 1.69396 6.74712 1.77291 6.59627C1.85185 6.44543 1.98738 6.33204 2.14979 6.28096L3.74156 5.78297ZM17.5492 0.0896144C17.7381 0.0302672 17.935 5.10465e-05 18.133 0H18.1332C18.5493 0.000299189 18.9545 0.133425 19.2897 0.38C19.6249 0.626574 19.8726 0.973717 19.9968 1.37087L20.4432 2.79852C20.5054 2.9976 20.486 3.21324 20.3893 3.39804C20.2926 3.58283 20.1264 3.72165 19.9274 3.78397L3.33352 8.97553H20.8947V8.9762C21.0846 8.9762 21.2667 9.05163 21.4009 9.1859C21.5352 9.32016 21.6106 9.50226 21.6106 9.69213V12.0446V13.4668V22.3253C21.6106 22.7695 21.4342 23.1955 21.1201 23.5096C20.806 23.8237 20.38 24.0001 19.9358 24.0001H2.83325C2.38907 24.0001 1.96309 23.8237 1.64901 23.5096C1.33493 23.1955 1.15848 22.7695 1.15848 22.3253V13.4668V12.0446V9.65322L0.478053 7.47698C0.323644 6.9827 0.371748 6.44734 0.611796 5.9885C0.851845 5.52966 1.26421 5.18486 1.7583 5.02986L17.5492 0.0896144ZM4.74819 12.0446H2.46939V10.2864H5.70147L4.74819 12.0446ZM9.7799 12.0446H6.30032L7.2536 10.2864H10.7332L9.7799 12.0446ZM14.8116 12.0446H11.332L12.2853 10.2864H15.7649L14.8116 12.0446ZM20.2997 12.0446H16.3637L17.317 10.2864H20.2997V12.0446Z"
                    fill="white"
                  />
                </svg>
                <h2
                  className="text-uppercase text-orange ms-3 my-0"
                  dangerouslySetInnerHTML={{
                    __html: t("HomeBlock1Title"),
                  }}
                />
              </div>

              <p
                dangerouslySetInnerHTML={{
                  __html: t("HomeBlock1Paragraph"),
                }}
              />
              <div className="my-5 d-block d-md-none">
                <ImageSlider images={images} />
              </div>
            </div>

            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="31"
                  height="24"
                  viewBox="0 0 31 24"
                  fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.5444 24C14.4638 24 16.2779 23.5494 17.8868 22.7482C19.0889 22.3117 20.4019 21.9217 21.7254 21.7102C23.7027 21.3943 25.6286 21.4889 27.233 22.3447L27.2351 22.3459L27.2373 22.347C27.3205 22.3906 27.4116 22.4175 27.5054 22.4259C27.5994 22.4342 27.6938 22.4239 27.7832 22.3957C27.8731 22.3673 27.9563 22.3215 28.0281 22.2611C28.0998 22.2007 28.1592 22.1266 28.2026 22.0427L28.2027 22.0425L28.2028 22.0424L30.3742 17.8336C30.4167 17.7525 30.4433 17.6637 30.4522 17.5723C30.4622 17.4703 30.4501 17.3683 30.4176 17.273C30.385 17.1773 30.3323 17.0891 30.2624 17.0147C30.1927 16.9405 30.1081 16.8822 30.0144 16.8435L30.0093 16.8414L30.0042 16.8394C29.2743 16.5517 26.5427 15.6478 23.6364 16.5874C24.2215 15.1741 24.5444 13.6248 24.5444 12C24.5444 5.37258 19.1719 0 12.5444 0C5.91702 0 0.544434 5.37258 0.544434 12C0.544434 18.6274 5.91702 24 12.5444 24ZM21.5041 20.3256C21.3768 20.346 21.2497 20.3678 21.1229 20.3911C21.6479 19.8544 22.1229 19.2687 22.5405 18.6415C23.1759 18.2433 23.8242 17.9792 24.4582 17.8131L23.3338 20.1475C22.7192 20.1657 22.106 20.2295 21.5041 20.3256ZM26.1287 17.5762C27.186 17.5437 28.1166 17.7236 28.7607 17.9029L27.262 20.8078C26.4901 20.4836 25.6826 20.2928 24.8631 20.2037L26.1287 17.5762ZM12.6432 10.6739C13.3766 10.7261 13.9289 11.363 13.8767 12.0964C13.8245 12.8299 13.1876 13.3821 12.4542 13.3299C11.7207 13.2778 11.1685 12.6409 11.2207 11.9074C11.2729 11.174 11.9097 10.6217 12.6432 10.6739ZM12.5441 7.67384C11.1429 7.67384 10.0021 6.53355 10.0021 5.13193C10.0021 3.73032 11.1423 2.58984 12.5441 2.58984C13.9459 2.58984 15.086 3.73014 15.086 5.13193C15.086 6.53373 13.9458 7.67384 12.5441 7.67384ZM17.2232 10.7693C17.61 10.9923 18.0487 11.11 18.4953 11.1105L18.4958 11.1114C18.8855 11.1108 19.2698 11.0205 19.6191 10.8477C19.9683 10.6749 20.2732 10.4241 20.51 10.1147C20.7469 9.80525 20.9095 9.44553 20.9852 9.06328C21.0609 8.68103 21.0478 8.28649 20.9467 7.91014C20.8457 7.53379 20.6594 7.18571 20.4025 6.8928C20.1455 6.59988 19.8246 6.36996 19.4646 6.22082C19.1045 6.07167 18.7151 6.00728 18.3262 6.03263C17.9374 6.05798 17.5596 6.17238 17.222 6.367C16.0078 7.06781 15.59 8.62538 16.2906 9.8392C16.5147 10.2255 16.8363 10.5462 17.2232 10.7693ZM18.4876 17.9794C18.0419 17.9788 17.6042 17.861 17.2185 17.6378C16.005 16.9363 15.5885 15.3783 16.2904 14.1649C16.4573 13.8758 16.6796 13.6225 16.9446 13.4194C17.2095 13.2163 17.5119 13.0674 17.8344 12.9812C18.1569 12.895 18.4932 12.8732 18.8242 12.917C19.1551 12.9609 19.4742 13.0696 19.7631 13.2368C20.9767 13.9383 21.3933 15.4961 20.6912 16.7097C20.467 17.0956 20.1455 17.4158 19.7588 17.6386C19.3722 17.8614 18.9338 17.9789 18.4876 17.9794ZM10.7403 20.6638C11.2167 21.1409 11.863 21.4092 12.5372 21.4097H12.5392C13.9397 21.4097 15.08 20.2707 15.0811 18.8694C15.0813 18.5354 15.0157 18.2047 14.888 17.8961C14.7604 17.5875 14.5732 17.3071 14.3371 17.0708C14.101 16.8346 13.8207 16.6472 13.5122 16.5193C13.2036 16.3915 12.8729 16.3257 12.539 16.3257C11.1383 16.3257 9.99707 17.4647 9.99707 18.866C9.99664 19.5401 10.264 20.1868 10.7403 20.6638ZM6.59675 17.9705C6.14978 17.9699 5.71079 17.8519 5.32375 17.6284C4.9367 17.4048 4.61516 17.0835 4.39133 16.6966C3.69161 15.4819 4.11053 13.9247 5.32489 13.225C5.66257 13.0306 6.04042 12.9164 6.42926 12.8912C6.81809 12.8661 7.2075 12.9307 7.5674 13.08C7.9273 13.2293 8.24806 13.4594 8.5049 13.7524C8.76175 14.0454 8.94779 14.3935 9.04867 14.7699C9.14954 15.1463 9.16255 15.5408 9.08669 15.923C9.01083 16.3051 8.84813 16.6648 8.61115 16.9741C8.37418 17.2834 8.06927 17.5341 7.71999 17.7068C7.37071 17.8795 6.9864 17.9696 6.59675 17.9701V17.9705ZM5.47459 10.8384C5.82427 11.0113 6.20903 11.1013 6.59908 11.1016C7.04509 11.101 7.48316 10.9836 7.86963 10.761C8.25611 10.5384 8.57751 10.2184 8.80178 9.83284C9.50404 8.61975 9.0884 7.06145 7.87549 6.35919C7.53798 6.16367 7.16001 6.04842 6.77083 6.02237C6.38164 5.99633 5.99168 6.06018 5.63113 6.20898C5.27057 6.35778 4.94908 6.58755 4.69155 6.8805C4.43402 7.17345 4.24734 7.52172 4.14596 7.89837C4.04459 8.27502 4.03123 8.66995 4.10694 9.05259C4.18264 9.43523 4.34536 9.79532 4.58252 10.105C4.81968 10.4147 5.12491 10.6656 5.47459 10.8384Z"
                    fill="white"
                  />
                </svg>
                <h2
                  className="text-uppercase text-orange ms-3 my-0"
                  dangerouslySetInnerHTML={{
                    __html: t("HomeBlock2Title"),
                  }}
                />
              </div>

              <p
                dangerouslySetInnerHTML={{
                  __html: t("HomeBlock2Paragraph"),
                }}
              />
              <div className="my-5 d-block d-md-none">
                <ImageSlider images={images} />
              </div>
            </div>

            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="24"
                  viewBox="0 0 28 24"
                  fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M26.2673 0H1.73252C0.973343 0 0.35791 0.615434 0.35791 1.37461V22.6254C0.35791 23.3846 0.973343 24 1.73252 24H26.2673C27.0265 24 27.6419 23.3846 27.6419 22.6254V1.37461C27.6419 0.615434 27.0265 0 26.2673 0ZM5.95246 2.7457H2.95772V4.91982H5.95246V2.7457ZM9.25215 2.7457H12.2469V4.91982H9.25215V2.7457ZM18.7634 2.7457H15.7687V4.91982H18.7634V2.7457ZM21.8422 2.7457H24.8369V4.91982H21.8422V2.7457ZM5.95246 19.0816H2.95772V21.2558H5.95246V19.0816ZM9.25215 19.0816H12.2469V21.2558H9.25215V19.0816ZM18.7634 19.0816H15.7687V21.2558H18.7634V19.0816ZM21.8422 19.0816H24.8369V21.2558H21.8422V19.0816Z"
                    fill="white"
                  />
                </svg>
                <h2
                  className="text-uppercase text-orange ms-3 my-0"
                  dangerouslySetInnerHTML={{
                    __html: t("HomeBlock3Title"),
                  }}
                />
              </div>

              <p
                dangerouslySetInnerHTML={{
                  __html: t("HomeBlock3Paragraph"),
                }}
              />
              <Link to="/quiz">
                <button
                  onClick={() =>
                    trackEvent("user_interaction", "click", "take_quiz", "body")
                  }
                  className={`btn btn-orange text-white text-uppercase d-block fw-bold mt-2 gradient-orange mx-auto mt-md-0 mt-5`}>
                  <div className="d-flex align-items-center">
                    {t("HomeCTA")} <img src={imgArrowRight} />
                  </div>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Top5 baseDomain={baseDomain} t={t} />
      <div className="disclaimer text-center mt-5">
        <div className="container narrower">
          <h3 className="text-orange text-uppercase">{t("Disclaimer")}</h3>

          <ExpandableText text={t("DisclaimerParagraph")} />
        </div>
      </div>
      {!isDesktopOrLaptop && (
        <AnimatePresence>
          {isBadgeVisible && (
            <motion.div
              onClick={() => goToQuiz()}
              className="badge-top5 text-white d-flex flex-column align-items-center justify-content-center"
              style={{ cursor: "pointer" }}
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ duration: 0.5 }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="20"
                viewBox="0 0 22 24"
                fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5138 1.43499C18.4036 1.3538 18.2702 1.31007 18.1333 1.31023L18.133 1.3109C18.0677 1.311 18.0029 1.32106 17.9406 1.34072L14.812 2.31953L16.1235 3.59968L19.0356 2.68869L18.7457 1.76135C18.7053 1.63056 18.624 1.51618 18.5138 1.43499ZM10.0164 3.81984L13.3327 2.78233L14.6442 4.06246L11.3279 5.09992L10.0164 3.81984ZM8.53714 4.28265L9.84858 5.56271L6.53215 6.6002L5.22083 5.32017L8.53714 4.28265ZM3.74156 5.78297L5.05286 7.06298L2.01873 8.01216L1.72865 7.08566C1.67805 6.92311 1.69396 6.74712 1.77291 6.59627C1.85185 6.44543 1.98738 6.33204 2.14979 6.28096L3.74156 5.78297ZM17.5492 0.0896144C17.7381 0.0302672 17.935 5.10465e-05 18.133 0H18.1332C18.5493 0.000299189 18.9545 0.133425 19.2897 0.38C19.6249 0.626574 19.8726 0.973717 19.9968 1.37087L20.4432 2.79852C20.5054 2.9976 20.486 3.21324 20.3893 3.39804C20.2926 3.58283 20.1264 3.72165 19.9274 3.78397L3.33352 8.97553H20.8947V8.9762C21.0846 8.9762 21.2667 9.05163 21.4009 9.1859C21.5352 9.32016 21.6106 9.50226 21.6106 9.69213V12.0446V13.4668V22.3253C21.6106 22.7695 21.4342 23.1955 21.1201 23.5096C20.806 23.8237 20.38 24.0001 19.9358 24.0001H2.83325C2.38907 24.0001 1.96309 23.8237 1.64901 23.5096C1.33493 23.1955 1.15848 22.7695 1.15848 22.3253V13.4668V12.0446V9.65322L0.478053 7.47698C0.323644 6.9827 0.371748 6.44734 0.611796 5.9885C0.851845 5.52966 1.26421 5.18486 1.7583 5.02986L17.5492 0.0896144ZM4.74819 12.0446H2.46939V10.2864H5.70147L4.74819 12.0446ZM9.7799 12.0446H6.30032L7.2536 10.2864H10.7332L9.7799 12.0446ZM14.8116 12.0446H11.332L12.2853 10.2864H15.7649L14.8116 12.0446ZM20.2997 12.0446H16.3637L17.317 10.2864H20.2997V12.0446Z"
                  fill="white"
                />
              </svg>
              <small>
                TOP 5<br />
                film Alter
                <br />
                egos
              </small>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
}

function goToQuiz() {
  // Get the section element by ID
  const section = document.getElementById("top5");

  // Scroll to the section using scrollIntoView()
  section.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  });

  // Prevent the default link behavior
  return false;
}

export default Home;
